import {testIdProps} from '@github-ui/test-id-props'
import {TriangleDownIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useImperativeHandle, useRef} from 'react'

import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useViews} from '../../hooks/use-views'
import {useUserNotices} from '../../state-providers/user-notices/user-notices-provider'
import {PotentiallyDirty} from '../potentially-dirty'
import styles from './menu-anchor.module.css'

type MenuAnchorProps = Omit<React.HTMLAttributes<HTMLElement>, 'aria-labelledby'> & {
  viewName: string
  open: boolean
  showDirtyIndicator?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export const DirtyViewIndicator = ({
  isViewStateDirty,
  children,
  onClick,
}: React.PropsWithChildren<{
  onClick?: React.MouseEventHandler<HTMLElement> | undefined
  isViewStateDirty: boolean
}>) => {
  const {hasWritePermissions} = ViewerPrivileges()
  return (
    <PotentiallyDirty
      isDirty={isViewStateDirty}
      hideDirtyState={!hasWritePermissions}
      onClick={onClick}
      {...(isViewStateDirty ? testIdProps('view-options-dirty') : undefined)}
    >
      {children}
    </PotentiallyDirty>
  )
}
export function MenuAnchor({
  ref: forwardedRef,
  viewName,
  open,
  onClick,
  showDirtyIndicator = true,
  ...buttonProps
}: MenuAnchorProps) {
  const {isViewStateDirty} = useViews()
  const {userNotices} = useUserNotices()

  const contentRef = useRef<HTMLAnchorElement>(null)
  useImperativeHandle<HTMLSpanElement | null, HTMLSpanElement | null>(forwardedRef, () => contentRef.current)

  const button = (
    <IconButton
      // User notices can be anchored to the view menu button. If one is present, prevent the tooltip from overlapping.
      tooltipDirection={userNotices.memex_issue_types_rename_prompt ? 'nw' : 'sw'}
      onClick={onClick}
      {...buttonProps}
      ref={contentRef}
      icon={TriangleDownIcon}
      aria-label={`View options for ${viewName}`}
      className={styles.menuAnchorButton}
      {...testIdProps('view-options-menu-toggle')}
    />
  )

  if (!showDirtyIndicator) {
    return button
  }

  return (
    <DirtyViewIndicator isViewStateDirty={isViewStateDirty} onClick={onClick}>
      {button}
    </DirtyViewIndicator>
  )
}
